import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Banknote, Eye } from 'lucide-react';
import { apiRequests } from '../../shared/api/apiRequests';
import { Pagination } from '../../features/Pagination';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';

const ReferralMembers = () => {
  const [members, setMembers] = useState({ items: [], totalCount: 0 });
  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState({ isAgreementSigned: null });
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const getData = async (count = 20, offset = 0) => {
    try {
      setLoading(true);
      const response = await apiRequests.referralProgram.getMembers(
        searchValue,
        filterValues.isAgreementSigned,
        count,
        offset,
      );
      setMembers(response);
    } catch (e) {
      console.error('Ошибка загрузки участников:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    getData();
  };

  const handleFilterChange = (key, value) => {
    if (key === 'isAgreementSigned') {
      const boolValue = value === null ? null : value === 'true';
      setFilterValues((prev) => ({ ...prev, [key]: boolValue }));
    } else {
      setFilterValues((prev) => ({ ...prev, [key]: value }));
    }
  };

  const applyFilters = () => {
    getData();
  };

  const resetFilters = () => {
    setFilterValues({ isAgreementSigned: null });
    setSearchValue('');
    getData();
  };

  useEffect(() => {
    getData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Не подписано';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const getUserName = (user) => {
    if (!user) return 'Без имени';
    const parts = [user.surname, user.name, user.patronymic].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'Без имени';
  };

  const filters = [
    {
      key: 'isAgreementSigned',
      type: 'select',
      label: 'Статус договора',
      value: filterValues.isAgreementSigned === null ? null : String(filterValues.isAgreementSigned),
      placeholder: 'Все участники',
      options: [
        { value: 'true', label: 'Договор подписан' },
        { value: 'false', label: 'Договор не подписан' },
      ],
    },
  ];

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск по ФИО..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
        rightContent={
          <Link to="/referral-program/withdrawal-requests" className="page-bar-clean__link-btn">
            <Banknote size={18} />
            <span>Заявки на выплату</span>
          </Link>
        }
      />

      <div className="mb-3">
        {loading ? (
          <p className="text-center">Загрузка...</p>
        ) : members.items?.length === 0 ? (
          <p className="text-center">По вашему запросу ничего не найдено</p>
        ) : (
          members.items?.map((member) => (
            <EntityCard
              key={member.referralUser.id}
              title={getUserName(member.referralUser.user)}
              subtitle={member.referralUser.user?.email || 'Не указан'}
              badge={member.referralUser.banInfo 
                ? { text: 'Заблокирован', variant: 'danger' }
                : member.referralUser.agreementSignedAt
                  ? { text: 'Договор подписан', variant: 'success' }
                  : { text: 'Без договора', variant: 'warning' }
              }
              fields={[
                { label: 'Заработано', value: formatCurrency(member.totalEarntInUSD) },
                { label: 'Клиентов', value: member.totalCustomers },
                { label: 'Заказов', value: member.totalOrders },
              ]}
              actions={
                <Link
                  to={`${location.pathname}/${member.referralUser.id}`}
                  className="entity-card__action"
                  title="Подробнее"
                >
                  <Eye size={20} />
                </Link>
              }
            />
          ))
        )}
      </div>

      {members.totalCount > 20 && (
        <Pagination
          count={members.totalCount}
          itemsPerPage={20}
          onChange={(page) => getData(20, 20 * page)}
        />
      )}
    </>
  );
};

export default ReferralMembers;
